import React, { useState, useRef } from 'react';
import { Box, Typography, Button, Modal, Backdrop, Fade, Grid } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/effect-coverflow';

// Icons
import StarBorderIcon from '@mui/icons-material/StarBorder';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import TheatersIcon from '@mui/icons-material/Theaters';
import TvIcon from '@mui/icons-material/Tv';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import VideocamIcon from '@mui/icons-material/Videocam';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import CampaignIcon from '@mui/icons-material/Campaign';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import Movies from '../../../assets/movies.png';
import TVShows from '../../../assets/TvSeries.png';
import Webseries from '../../../assets/Webseries.png';
import Documentary from '../../../assets/documnetary.png';
import NewsReporter from '../../../assets/NewsReporter.png';
import Ads from '../../../assets/Ads.png';
import Modeling from '../../../assets/Modeling.png';

import { useTheme } from '../../../ThemeContext';

const features = [
  { title: 'Modeling', description: 'Models and agencies can highlight their portfolios, connect with brands, and get recognized by the industry.', fullDetails: 'Industry users in modeling can document completed shoots, detailing brand collaborations, fashion events, commercial work, portfolio updates, agencies involved, and media presence.', image: Modeling, icon: <CheckroomIcon />, color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)' },
  { title: 'Movies', description: 'Film professionals can showcase their movies, including production details, cast, crew, distribution, and industry recognition.', fullDetails: 'This feature allows experienced industry users to document completed film projects. They can add essential details like the film title, genre, production company, director, cast, crew, release date, distribution platforms, and industry achievements.', image: Movies, icon: <TheatersIcon />, color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' },
  { title: 'TV Drama', description: 'TV professionals can list their drama series, including production details, network, cast, crew, episodes, and achievements.', fullDetails: 'This feature allows industry users to document completed TV drama projects by adding details such as title, genre, broadcast network, production company, director, writers, cast, crew, episode count, release year, and audience reception.', image: TVShows, icon: <TvIcon />, color: '#ec4899', bg: 'rgba(236,72,153,0.1)' },
  { title: 'Web Series', description: 'Web series creators can document past projects, including production details, streaming platforms, cast, crew, episodes, and achievements.', fullDetails: 'This feature enables industry professionals to list completed web series with details such as title, genre, streaming platform, production company, director, writers, cast, crew, episode count, release date, and audience reception.', image: Webseries, icon: <OndemandVideoIcon />, color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
  { title: 'Documentary', description: 'Filmmakers can showcase past documentaries, including research, production details, distribution, and impact on audiences.', fullDetails: 'This feature allows industry professionals to document completed documentary projects with details such as title, theme, production company, director, research sources, filming locations, release platforms, and audience reception.', image: Documentary, icon: <VideocamIcon />, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
  { title: 'News Reporter', description: 'Journalists can document past news coverage, including publication details, reporting style, investigation process, and audience impact.', fullDetails: 'This feature allows industry professionals to showcase completed news reports with details such as headline, publication platform, report type, sources used, coverage locations, release date, and audience engagement.', image: NewsReporter, icon: <NewspaperIcon />, color: '#6366f1', bg: 'rgba(99,102,241,0.1)' },
  { title: 'Advertising', description: 'Marketers can showcase past ad campaigns, including brand collaborations, creative execution, distribution, and audience impact.', fullDetails: 'This feature allows industry professionals to document completed advertising projects, covering campaign title, brand association, target audience, marketing strategy, distribution channels, creative direction, and engagement metrics.', image: Ads, icon: <CampaignIcon />, color: '#14b8a6', bg: 'rgba(20,184,166,0.1)' },
];

const Features = () => {
  const { isDarkMode } = useTheme();
  const [openIndex, setOpenIndex] = useState(null);
  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(1);

  const handleOpen = (index) => { setOpenIndex(index); swiperRef.current?.autoplay?.stop(); };
  const handleClose = () => { setOpenIndex(null); swiperRef.current?.autoplay?.start(); };

  return (
    <Box sx={{ py: 10, px: { xs: 2, md: 6 }, backgroundColor: isDarkMode ? '#050a14' : '#fafbfe', position: 'relative' }}>
      
      {/* Header Section */}
      <Box textAlign="center" mb={6}>
        <Box sx={{ 
          display: 'inline-flex', alignItems: 'center', gap: 1, px: 2.5, py: 0.7, 
          borderRadius: '30px', border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
          background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#ffffff', mb: 3 
        }}>
          <StarBorderIcon sx={{ color: '#6366f1', fontSize: '1.2rem' }} />
          <Typography sx={{ color: isDarkMode ? '#fff' : '#0f172a', fontSize: '0.85rem', fontWeight: 700 }}>
            Powerful Tools for Every Creator
          </Typography>
        </Box>
        
        <Typography variant="h2" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a', mb: 2, fontSize: { xs: '2rem', md: '3.5rem' } }}>
          Creative & <Box component="span" sx={{ color: '#4f46e5' }}>Professional</Box> Platform Tools
        </Typography>
        
        <Box sx={{ width: 80, height: 4, background: 'linear-gradient(90deg, #6366f1, #e2e8f0)', mx: 'auto', mt: 3, mb: 3, borderRadius: '2px' }} />
        
        <Typography variant="body1" sx={{ color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#64748b', fontSize: '1.1rem' }}>
          Explore powerful resources designed for the film industry.
        </Typography>
      </Box>

      {/* Swiper Cards */}
      <Box sx={{ maxWidth: '1250px', mx: 'auto', mb: 8 }}>
        <Swiper
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
          spaceBetween={30}
          slidesPerView={1}
          breakpoints={{ 
            768: { slidesPerView: 2 }, 
            1024: { slidesPerView: 3 } 
          }}
          autoplay={{ delay: 2500, disableOnInteraction: false }}
          loop={true}
          modules={[Autoplay]}
          centeredSlides={true}
        >
          {features.map((feature, index) => {
            // Determine active based on center slide
            const total = features.length;
            const virtualIndex = (index - activeIndex + total) % total;
            const isActive = virtualIndex === 0;

            return (
              <SwiperSlide key={index} style={{ padding: '20px 0' }}>
                <Box
                  sx={{
                    background: isDarkMode ? '#101726' : '#ffffff',
                    border: isActive 
                      ? '2px solid #3b82f6' 
                      : (isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f1f5f9'),
                    borderRadius: '24px',
                    p: 2,
                    boxShadow: isActive 
                      ? '0 20px 50px rgba(59,130,246,0.15)' 
                      : (isDarkMode ? 'none' : '0 10px 30px rgba(0,0,0,0.03)'),
                    transform: isActive ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {/* Image Container with Padding */}
                  <Box sx={{ 
                    position: 'relative', 
                    borderRadius: '16px', 
                    overflow: 'hidden',
                    mb: 3,
                    height: '200px',
                    background: isDarkMode ? '#0a0f18' : '#f8fafc'
                  }}>
                    <Box
                      component="img"
                      src={feature.image}
                      alt={feature.title}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    {/* Floating Icon Top Left */}
                    <Box sx={{
                      position: 'absolute',
                      top: 12,
                      left: 12,
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      background: 'rgba(255,255,255,0.9)',
                      backdropFilter: 'blur(4px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: feature.color,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                      {feature.icon}
                    </Box>
                  </Box>

                  {/* Content Area */}
                  <Box sx={{ px: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                      {/* Circle Icon left of Title */}
                      <Box sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background: feature.bg,
                        color: feature.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        {feature.icon}
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a', mb: 0.5 }}>
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#64748b', lineHeight: 1.6 }}>
                          {feature.description}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Button */}
                    <Box sx={{ mt: 'auto', textAlign: 'center', pt: 2 }}>
                      <Button
                        variant={isActive ? "contained" : "outlined"}
                        onClick={() => handleOpen(index)}
                        endIcon={<KeyboardArrowRightIcon />}
                        sx={{
                          borderRadius: '30px',
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 3,
                          py: 0.8,
                          background: isActive ? '#3b82f6' : 'transparent',
                          color: isActive ? '#fff' : feature.color,
                          borderColor: isActive ? 'transparent' : feature.bg,
                          boxShadow: isActive ? '0 8px 20px rgba(59,130,246,0.3)' : 'none',
                          '&:hover': {
                            background: isActive ? '#2563eb' : feature.bg,
                            borderColor: isActive ? 'transparent' : feature.bg,
                          }
                        }}
                      >
                        See More
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </Box>

      {/* Bottom Highlights Banner */}
      <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
        <Box sx={{ 
          background: isDarkMode ? '#101726' : '#ffffff', 
          borderRadius: '24px', 
          p: { xs: 3, md: 4 },
          boxShadow: isDarkMode ? 'none' : '0 12px 40px rgba(0,0,0,0.03)',
          border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : 'none'
        }}>
          <Grid container spacing={4} alignItems="center">
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(99,102,241,0.1)', color: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <VerifiedUserOutlinedIcon />
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a' }}>Trusted Platform</Typography>
                  <Typography variant="body2" sx={{ color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b', fontSize: '0.75rem' }}>Verified creators and industry professionals</Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(59,130,246,0.1)', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <BoltOutlinedIcon />
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a' }}>All-in-One Solution</Typography>
                  <Typography variant="body2" sx={{ color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b', fontSize: '0.75rem' }}>Everything you need to grow your creative career</Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(16,185,129,0.1)', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PeopleOutlineIcon />
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a' }}>Industry Connections</Typography>
                  <Typography variant="body2" sx={{ color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b', fontSize: '0.75rem' }}>Connect with top talent and opportunities</Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(245,158,11,0.1)', color: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <StarBorderIcon />
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a' }}>Professional Growth</Typography>
                  <Typography variant="body2" sx={{ color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b', fontSize: '0.75rem' }}>Tools and resources to elevate your craft</Typography>
                </Box>
              </Box>
            </Grid>

          </Grid>
        </Box>
      </Box>

      {/* Modal */}
      <Modal open={openIndex !== null} onClose={handleClose} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 300 }}>
        <Fade in={openIndex !== null}>
          <Box
            sx={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', md: 600 },
              background: isDarkMode ? 'rgba(10,14,26,0.97)' : '#ffffff',
              backdropFilter: 'blur(20px)',
              border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : 'none',
              borderRadius: '24px',
              boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
              p: 4,
              outline: 'none',
            }}
          >
            {openIndex !== null && (
              <>
                <Typography variant="h5" fontWeight={800} gutterBottom sx={{ color: isDarkMode ? 'white' : '#0f172a' }}>
                  {features[openIndex].title}
                </Typography>
                <Box sx={{ height: '3px', width: 60, background: 'linear-gradient(90deg, #6366f1, #3b82f6)', mb: 3, borderRadius: '2px' }} />
                <Box sx={{ borderRadius: '16px', overflow: 'hidden', mb: 3 }}>
                  <img src={features[openIndex].image} alt={features[openIndex].title} style={{ width: '100%', height: 220, objectFit: 'cover' }} />
                </Box>
                <Typography variant="body1" sx={{ color: isDarkMode ? '#b0b8c4' : '#475569', lineHeight: 1.7 }}>
                  {features[openIndex].fullDetails}
                </Typography>
                <Box textAlign="right" mt={4}>
                  <Button
                    onClick={handleClose}
                    variant="contained"
                    sx={{
                      background: '#3b82f6',
                      color: 'white',
                      borderRadius: '30px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 4,
                      '&:hover': { background: '#2563eb' },
                    }}
                  >
                    Close
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default Features;
